import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { toast } from 'sonner'
import type { SaveRuleInput, NotificationEvent } from '@/lib/api/modules/notifications'

export const NOTIFICATION_KEYS = {
  discordStatus: ['discord-bot-status'] as const,
  discordChannels: ['discord-bot-channels'] as const,
  rules: ['notification-rules'] as const,
}

export function useDiscordBotStatus() {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.discordStatus,
    queryFn: () => api.notifications.getDiscordStatus(),
  })
}

export function useDiscordChannels(enabled: boolean) {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.discordChannels,
    queryFn: () => api.notifications.getDiscordChannels(),
    enabled,
  })
}

export function useNotificationRules() {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.rules,
    queryFn: () => api.notifications.getNotificationRules(),
  })
}

export function useConnectDiscord() {
  return useMutation({
    mutationFn: async () => {
      const { url } = await api.notifications.getDiscordConnectUrl()
      window.location.href = url
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao conectar com o Discord')
    },
  })
}

export function useDisconnectDiscord() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.notifications.disconnectDiscord(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.discordStatus })
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.discordChannels })
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.rules })
      toast.success('Discord desconectado')
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao desconectar Discord')
    },
  })
}

export function useTestDiscordChannel() {
  return useMutation({
    mutationFn: (channelId: string) => api.notifications.testDiscordChannel(channelId),
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Mensagem de teste enviada')
      } else {
        toast.error(data.error || 'Falha ao enviar teste')
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao enviar teste')
    },
  })
}

export function useSaveNotificationRules() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (rules: SaveRuleInput[]) => api.notifications.saveNotificationRules(rules),
    onMutate: async (newRules) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_KEYS.rules })

      const previousRules = queryClient.getQueryData<NotificationEvent[]>(NOTIFICATION_KEYS.rules)

      // Optimistic update: rebuild the events list from the new rules
      if (previousRules) {
        const updated = previousRules.map((event) => {
          const rulesForEvent = newRules.filter((r) => r.eventType === event.eventType)

          // If no rules target this event, keep existing destinations
          if (rulesForEvent.length === 0) {
            return event
          }

          // Build new destinations from rules
          const newDestinations = rulesForEvent.map((r) => ({
            ruleId: `optimistic-${r.eventType}-${r.channelId}`,
            provider: r.provider,
            channelId: r.channelId,
            channelName: r.channelName,
            isActive: r.isActive,
          }))

          return { ...event, destinations: newDestinations }
        })

        queryClient.setQueryData(NOTIFICATION_KEYS.rules, updated)
      }

      return { previousRules }
    },
    onError: (_error, _newRules, context) => {
      if (context?.previousRules) {
        queryClient.setQueryData(NOTIFICATION_KEYS.rules, context.previousRules)
      }
      toast.error('Erro ao salvar regras de notificacao')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.rules })
    },
  })
}
